// src/view/ListarAprendices.jsx
import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import Swal from "sweetalert2";
import DataTable from "../components/DataTable";

const ListarAprendices = () => {
  const [aprendices, setAprendices] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Función para descargar archivos docx
  const descargarDocx = async (url, nombreArchivo) => {
    try {
      const response = await axios.get(url, { responseType: "blob" });
      const urlBlob = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = urlBlob;
      link.setAttribute("download", nombreArchivo);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(urlBlob);
    } catch (error) {
      console.error("Error al descargar el documento:", error);
      Swal.fire("Error", "No se pudo generar el documento.", "error");
    }
  };

  // Función para eliminar un aprendiz (MISMO ESTILO QUE TU EJEMPLO)
  const deleteAprendiz = async (id) => {
    try {
      Swal.fire({
        title: "¿Está seguro?",
        text: "¡No podrá revertir este proceso!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
      }).then(async (result) => {
        if (result.isConfirmed) {
          const { data } = await axios.delete(`/api/aprendices/${id}`);
          await getData(); // Refrescar datos (igual que ListaInstructores)

          Swal.fire({
            icon: "success",
            title: data.message,
            showConfirmButton: false,
            timer: 1500,
          });
        }
      });
    } catch (error) {
      console.error("Error al eliminar el aprendiz:", error.message);
      Swal.fire("Error", "No se pudo eliminar el aprendiz.", "error");
    }
  };

  // Función para obtener los datos (MISMO PATRÓN QUE TU EJEMPLO)
  const getData = useCallback(async () => {
    try {
      setLoading(true);

      const { data } = await axios.get("/api/aprendices/listall/");
      if (!data || !Array.isArray(data.aprendices)) {
        throw new Error("Datos de aprendices no válidos.");
      }

      // ✅ MOSTRAR ÚLTIMO REGISTRO PRIMERO (más nuevo arriba)
      // Preferimos createdAt si existe; si no, usamos el timestamp del _id de MongoDB
      const aprendicesList = [...data.aprendices].sort((a, b) => {
        const ta = a?.createdAt ? new Date(a.createdAt).getTime() : parseInt(String(a._id).slice(0, 8), 16) * 1000;
        const tb = b?.createdAt ? new Date(b.createdAt).getTime() : parseInt(String(b._id).slice(0, 8), 16) * 1000;
        return tb - ta; // DESC
      });

      // 1) IDs
      const ids = aprendicesList.map((aprendiz) => aprendiz._id);

      // 2) Consultar tests Kolb masivo
      let kolbMap = {};
      try {
        const resTests = await axios.post("/api/testkolb/existencias", { ids });
        if (resTests.data?.ok && resTests.data?.kolbMap) {
          kolbMap = resTests.data.kolbMap;
        }
      } catch (err) {
        console.error("Error al obtener los testKolb masivamente:", err.message);
      }

      // 3) Formatear filas para DataTable
      const aprendicesConFunciones = (aprendicesList || []).map((aprendiz) => {
        const testId = kolbMap[aprendiz._id] || null;

        return {
          ...aprendiz,

          imagen: (
            <div className="d-flex justify-content-end justify-content-md-center align-items-center w-100">
              {aprendiz.imgAprendiz ? (
                <img
                  src={aprendiz.imgAprendiz}
                  alt="Aprendiz"
                  className="rounded-circle border border-2"
                  style={{ width: 46, height: 46, objectFit: "cover" }}
                />
              ) : (
                <div
                  className="rounded-circle border border-2 d-flex justify-content-center align-items-center text-secondary bg-body-tertiary"
                  style={{ width: 46, height: 46 }}
                  title="Sin foto"
                >
                  <i className="fa-solid fa-user"></i>
                </div>
              )}
            </div>
          ),
          funciones: (
            <div className="row g-2 align-items-center justify-content-end">
              {/* SELECT: arriba en móvil, izquierda en md+ */}
              <div className="col-12 col-md-auto order-0 order-md-0">
                <select
                  className="form-select form-select-sm w-100"
                  style={{ minWidth: 190, height: 34 }}
                  defaultValue=""
                  onChange={(e) => {
                    const modelo = e.target.value;
                    if (modelo) {
                      descargarDocx(
                        `/api/hojavida/generar-docx/${aprendiz._id}?modelohv=${modelo}`,
                        `hoja_vida_${modelo}_${aprendiz.documentoAprendiz}.docx`
                      );
                      e.target.value = "";
                    }
                  }}
                  title="Selecciona Hoja de Vida"
                >
                  <option value="" disabled>
                    Hoja de Vida…
                  </option>
                  <option value="modelo1">Modelo 1</option>
                  <option value="modelo2">Modelo 2</option>
                  <option value="modelo3">Modelo 3</option>
                  <option value="modelo4">Modelo 4</option>
                  <option value="modelo5">Modelo 5</option>
                </select>
              </div>

              {/* BOTONES: abajo en móvil, derecha en md+ */}
              <div className="col-12 col-md-auto order-1 order-md-1">
                <div className="d-flex gap-2 flex-wrap justify-content-end justify-content-md-end">
                  {/* Kolb */}
                  {testId ? (
                    <button
                      className="btn btn-warning btn-sm d-inline-flex align-items-center justify-content-center"
                      style={{ width: 34, height: 34 }}
                      onClick={() =>
                        navigate(`/aprendiz/testkolb/editar/${testId}`, { state: { aprendiz } })
                      }
                      title="Editar Test de Kolb"
                      type="button"
                    >
                      <i className="fas fa-brain"></i>
                    </button>
                  ) : (
                    <button
                      className="btn btn-sm d-inline-flex align-items-center justify-content-center"
                      style={{ width: 34, height: 34, backgroundColor: "#6f42c1", color: "#fff" }}
                      onClick={() =>
                        navigate(`/aprendiz/testkolb/registrar/${aprendiz._id}`, { state: { aprendiz } })
                      }
                      title="Registrar Test de Kolb"
                      type="button"
                    >
                      <i className="fas fa-brain"></i>
                    </button>
                  )}

                  {/* Compromiso */}
                  <button
                    className="btn btn-primary btn-sm d-inline-flex align-items-center justify-content-center"
                    style={{ width: 34, height: 34 }}
                    onClick={() =>
                      descargarDocx(
                        `/api/compromiso/generar-docx/${aprendiz._id}`,
                        `compromiso_${aprendiz.documentoAprendiz}.docx`
                      )
                    }
                    title="Compromiso del Aprendiz"
                    type="button"
                  >
                    <i className="fa-solid fa-file-word"></i>
                  </button>

                  {/* Tratamiento */}
                  <button
                    className="btn btn-secondary btn-sm d-inline-flex align-items-center justify-content-center"
                    style={{ width: 34, height: 34 }}
                    onClick={() =>
                      descargarDocx(
                        `/api/tratamiento/generar-docx/${aprendiz._id}`,
                        `tratamiento_${aprendiz.documentoAprendiz}.docx`
                      )
                    }
                    title="Tratamiento de Datos"
                    type="button"
                  >
                    <i className="fa-solid fa-file-word"></i>
                  </button>

                  {/* Editar */}
                  <button
                    className="btn btn-warning btn-sm d-inline-flex align-items-center justify-content-center"
                    style={{ width: 34, height: 34 }}
                    onClick={() => navigate(`/aprendiz/edit/${aprendiz._id}`, { state: { aprendiz } })}
                    title="Editar Aprendiz"
                    type="button"
                  >
                    <i className="fa-solid fa-pen-to-square"></i>
                  </button>

                  {/* Eliminar */}
                  <button
                    className="btn btn-danger btn-sm d-inline-flex align-items-center justify-content-center"
                    style={{ width: 34, height: 34 }}
                    onClick={() => deleteAprendiz(aprendiz._id)}
                    title="Eliminar Aprendiz"
                    type="button"
                  >
                    <i className="fa-solid fa-trash"></i>
                  </button>
                </div>
              </div>
            </div>
          ),
        };
      });

      setAprendices(aprendicesConFunciones);
    } catch (error) {
      console.error("Error al cargar los aprendices:", error.message);
      Swal.fire("Error", "No se pudieron cargar los aprendices.", "error");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    getData();
  }, [getData]);

  const columns = useMemo(
    () => [
      { accessor: "imagen" },
      { Header: "Documento", accessor: "documentoAprendiz" },
      { Header: "Nombre", accessor: "nombre1Aprendiz" },
      { Header: "Apellido", accessor: "apellido1Aprendiz" },
      { Header: "Edad", accessor: "edadAprendiz" },
      { Header: "Sexo", accessor: "sexoAprendiz" },
      {
        Header: "Email",
        accessor: "emailAprendiz",
        width: 280,
        hideHeaderOnMobile: true,
      },
      { accessor: "funciones" },
    ],
    []
  );

  return (
    <div>
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          {/* Header */}
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">Aprendices</h5>
                <small className="text-secondary">Gestión institucional y generación de documentos.</small>
              </div>

              <div className="d-flex gap-2 flex-wrap">
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={() => getData()}
                  title="Actualizar"
                  disabled={loading}
                >
                  <i className={`fa-solid ${loading ? "fa-spinner fa-spin" : "fa-rotate"} me-2`}></i>
                  Actualizar
                </button>

                <button
                  className="btn btn-dark"
                  type="button"
                  onClick={() => navigate("/aprendiz/nuevo")}
                  title="Crear Aprendiz"
                >
                  <i className="fa-solid fa-plus me-2"></i>
                  Nuevo
                </button>
              </div>
            </div>
          </div>

          {/* Body */}
          <div>
            {loading ? (
              <div className="text-center text-secondary py-5">
                <div className="spinner-border" role="status" aria-hidden="true"></div>
                <div className="mt-3">Cargando aprendices…</div>
              </div>
            ) : Array.isArray(aprendices) && aprendices.length > 0 ? (
              <div className="overflow-hidden">
                <DataTable columns={columns} data={aprendices} />
              </div>
            ) : (
              <div className="text-center text-secondary py-5">
                <div className="display-6 mb-2">🐾</div>
                <div className="fw-semibold">No hay aprendices registrados</div>
                <div className="small mb-3">Crea el primer registro para iniciar la gestión.</div>
                <button
                  className="btn btn-dark"
                  type="button"
                  onClick={() => navigate("/aprendiz/registraraprendices")}
                >
                  <i className="fa-solid fa-plus me-2"></i>
                  Registrar aprendiz
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListarAprendices;